import {
    ChangeDetectorRef,
    InjectionToken,
    OnDestroy,
    Pipe,
    PipeTransform,
    Provider,
    inject,
    isDevMode,
} from "@angular/core";
import {
    TRANSLOCO_SCOPE,
    TranslocoService,
    provideTranslocoScope,
} from "@jsverse/transloco";
import { toCamelCase } from "@jsverse/utils";
import { Subscription, firstValueFrom } from "rxjs";
import { I18nObservabilityService } from "@app/shared/i18n/i18n-observability.service";

// The Translation scope module (ADR-015). All frontend translation goes through it, so the
// scope path, its alias and the load order are decided in this one file.

/** A Translation scope: the folder (relative to src/app) whose i18n/<lang>.json it loads. */
export interface TranslationScope {
    readonly path: string;
}

/** Declares a scope by its folder path. Each i18n/ folder exports one from its scope.ts. */
export function defineTranslationScope(path: string): TranslationScope {
    return { path };
}

/** Whether t() before ready() throws (development) or reports and returns the raw key. */
export const TRANSLATION_DEV_MODE = new InjectionToken<boolean>("TRANSLATION_DEV_MODE", {
    providedIn: "root",
    factory: () => isDevMode(),
});

/** Imperative translation in one scope: await ready() once, then call the sync t(). */
export interface ScopedTranslation {
    ready(): Promise<void>;
    t(key: string, params?: Record<string, unknown>): string;
}

// Transloco's default alias: the camelCase of the whole path. Transloco stores a loaded
// scope's keys under it whether the pipe or an imperative load runs first.
function aliasOf(scope: TranslationScope): string {
    return toCamelCase(scope.path);
}

/**
 * Makes `scope` the nearest scope for the `| t` pipe in this component's template. Put it in
 * `viewProviders`, not `providers`: a pipe in the caller's binding on this component's element
 * (`[label]="'key' | t"`) or in content the caller projects into it must keep the caller's scope.
 */
export function provideTranslationScope(scope: TranslationScope): Provider[] {
    return provideTranslocoScope(scope.path);
}

/**
 * `{{ 'key' | t }}` translates a key of the nearest provided scope. Pass params as the first
 * argument and another scope as the second for a key of that scope. Renders "" until the
 * scope loads, like Transloco's own pipe.
 */
@Pipe({ name: "t", pure: false })
export class TranslatePipe implements PipeTransform, OnDestroy {
    private readonly transloco = inject(TranslocoService);
    private readonly cdr = inject(ChangeDetectorRef);
    // TRANSLOCO_SCOPE is a multi token, so the nearest element injector yields an array of
    // the paths provideTranslationScope() registered.
    private readonly provided = inject(TRANSLOCO_SCOPE, { optional: true }) as unknown as string[] | null;
    private subscription: Subscription | null = null;
    private lastKey: string | undefined;
    private lastValue = "";

    transform(key: string, params?: Record<string, unknown>, scope?: TranslationScope): string {
        const path = scope?.path ?? this.nearestScopePath();
        const scopedKey = `${aliasOf({ path })}.${key}`;
        const cacheKey = params ? `${scopedKey}${JSON.stringify(params)}` : scopedKey;
        if (cacheKey === this.lastKey) {
            return this.lastValue;
        }
        this.lastKey = cacheKey;

        // The active language only, once: a language switch reloads the page (ADR-010), so
        // the pipe does not follow langChanges$.
        const lang = this.transloco.getActiveLang();
        this.subscription?.unsubscribe();
        this.subscription = this.transloco.load(`${path}/${lang}`).subscribe(() => {
            this.lastValue = this.transloco.translate(scopedKey, params, lang);
            this.cdr.markForCheck();
        });
        return this.lastValue;
    }

    ngOnDestroy(): void {
        this.subscription?.unsubscribe();
        this.subscription = null;
    }

    private nearestScopePath(): string {
        const nearest = this.provided?.at(-1);
        if (!nearest) {
            throw new Error("The | t pipe found no translation scope. Add provideTranslationScope().");
        }
        return nearest;
    }
}

/** Imperative translation for `scope`. Call it in an injection context. */
export function injectTranslation(scope: TranslationScope): ScopedTranslation {
    const transloco = inject(TranslocoService);
    const devMode = inject(TRANSLATION_DEV_MODE);
    const i18nObservability = inject(I18nObservabilityService);
    const alias = aliasOf(scope);
    let isReady = false;

    return {
        async ready() {
            await firstValueFrom(transloco.load(`${scope.path}/${transloco.getActiveLang()}`));
            isReady = true;
        },
        t(key, params) {
            const scopedKey = `${alias}.${key}`;
            if (!isReady) {
                if (devMode) {
                    throw new Error(
                        `Translation "${key}" of scope "${scope.path}" was requested before ready() resolved.`
                    );
                }
                i18nObservability.reportTranslateBeforeReady(scopedKey, scope.path);
                return scopedKey;
            }
            return transloco.translate(scopedKey, params);
        },
    };
}
